// src/components/ExamDateField.tsx
//
// Fix-campaign F2 (QA-001) — masked gg/mm/aaaa exam-date control replacing the
// native <input type="date"> on the registration screen. The native input
// follows the BROWSER locale for segment order, so a non-Italian locale showed
// mm/dd/yyyy and day-first typing ("31/12/2026") produced badInput + value=""
// → the student registered believing the date was saved (silent loss).
//
// Design constraints:
//   - Pure string masking + manual calendar validation — no Date round-trips
//     for the value, no Intl, no native date widget → browser locale cannot
//     influence parsing, and there is no timezone drift by construction.
//   - Emits ISO `yyyy-mm-dd` (or '') through onChange — the exact format the
//     old native input produced, so App.tsx's submit contract
//     (`${date}T00:00:00.000Z`) and profile storage stay untouched.
//   - Hydrates from an ISO `value` prop back to gg/mm/aaaa display.
//   - Invalid/partial input is SURFACED (inline message + aria-invalid), never
//     swallowed; a valid date echoes a human confirmation ("31 dicembre 2026").

import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTokens } from '../state/theme';

const MONTH_NAMES_IT = [
  'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre',
];

function daysInMonth(month: number, year: number): number {
  if (month === 2) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return leap ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

/** Format raw digits as a masked gg/mm/aaaa string (slashes auto-inserted). */
export function maskDateDigits(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

export type ParsedExamDate =
  | { kind: 'empty' }
  | { kind: 'partial' }
  | { kind: 'invalid' }
  | { kind: 'valid'; iso: string; day: number; month: number; year: number };

/** Parse a masked gg/mm/aaaa string. Pure, locale-free, TZ-free. */
export function parseMaskedDate(masked: string): ParsedExamDate {
  const digits = masked.replace(/\D/g, '');
  if (digits.length === 0) return { kind: 'empty' };
  if (digits.length < 8) return { kind: 'partial' };
  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4, 8));
  if (month < 1 || month > 12) return { kind: 'invalid' };
  if (day < 1 || day > daysInMonth(month, year)) return { kind: 'invalid' };
  // Sanity window: an exam date is near-present. Wildly off years are typos.
  if (year < 2020 || year > 2035) return { kind: 'invalid' };
  const iso = `${digits.slice(4, 8)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`;
  return { kind: 'valid', iso, day, month, year };
}

/** Render an ISO yyyy-mm-dd value as the masked gg/mm/aaaa display string. */
export function isoToMasked(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

export interface ExamDateFieldProps {
  /** ISO yyyy-mm-dd or ''. */
  value: string;
  /** Fires with ISO yyyy-mm-dd when the typed date is valid, '' otherwise. */
  onChange: (iso: string) => void;
  disabled?: boolean;
  inputId?: string;
  inputTestId?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  onValidityChange?: (kind: ParsedExamDate['kind']) => void;
  validationError?: string | null;
}

export function ExamDateField({
  value,
  onChange,
  disabled,
  inputId,
  inputTestId,
  inputRef,
  onValidityChange,
  validationError,
}: ExamDateFieldProps) {
  const tokens = useTokens();
  const generatedId = useId().replace(/:/g, '');
  const resolvedInputId = inputId ?? `exam-date-${generatedId}`;
  const feedbackId = `${resolvedInputId}-feedback`;
  // Local display state so partial input doesn't fight the ISO prop; hydrate
  // once from the prop (registration mounts with '' or a stored ISO).
  const [display, setDisplay] = useState(() => isoToMasked(value));
  const parsed = useMemo(() => parseMaskedDate(display), [display]);
  const lastReportedKind = useRef<ParsedExamDate['kind'] | null>(null);

  // Initial registration plus programmatic remount/reseed reporting. The
  // change handler reports synchronously as well, so a change+submit in one
  // React tick cannot mistake partial/invalid text for an explicit clear.
  useEffect(() => {
    if (lastReportedKind.current === parsed.kind) return;
    lastReportedKind.current = parsed.kind;
    onValidityChange?.(parsed.kind);
  }, [onValidityChange, parsed.kind]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = maskDateDigits(e.target.value);
    setDisplay(masked);
    const p = parseMaskedDate(masked);
    if (lastReportedKind.current !== p.kind) {
      lastReportedKind.current = p.kind;
      onValidityChange?.(p.kind);
    }
    onChange(p.kind === 'valid' ? p.iso : '');
  };

  const showError = parsed.kind === 'invalid' || Boolean(validationError);
  const showHint = parsed.kind === 'partial';

  return (
    <div>
      <div
        style={{
          background:   tokens.surface,
          border:       `1px solid ${showError ? tokens.wrong : tokens.lineStrong}`,
          borderRadius: tokens.radius.md,
          padding:      '10px 14px',
          minHeight:    44,
          display:      'flex',
          alignItems:   'center',
          boxSizing:    'border-box',
          marginTop:    4,
        }}
      >
        <input
          id={resolvedInputId}
          data-testid={inputTestId}
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={display}
          onChange={handleChange}
          placeholder="gg/mm/aaaa"
          disabled={disabled}
          aria-label="Data dell'esame (giorno/mese/anno)"
          aria-invalid={showError}
          aria-describedby={feedbackId}
          autoComplete="off"
          style={{
            border:     'none',
            outline:    'none',
            background: 'transparent',
            width:      '100%',
            color:      tokens.text,
            fontFamily: tokens.fonts.body,
            fontSize:   15,
            minHeight:  24,
          }}
        />
      </div>
      <p
        id={feedbackId}
        role={showError ? 'alert' : undefined}
        style={{
          margin:     '4px 0 0',
          fontFamily: tokens.fonts.body,
          fontSize:   12.5,
          lineHeight: 1.45,
          color:      showError ? tokens.wrongText : tokens.textFaint,
          minHeight:  18,
        }}
      >
        {validationError ?? (parsed.kind === 'invalid'
          ? 'Data non valida — controlla giorno, mese e anno (gg/mm/aaaa).'
          : null)}
        {!validationError && showHint && 'Continua: giorno, mese e anno (gg/mm/aaaa).'}
        {!validationError && parsed.kind === 'valid' &&
          `Esame: ${parsed.day} ${MONTH_NAMES_IT[parsed.month - 1]} ${parsed.year}`}
        {!validationError && parsed.kind === 'empty' && 'Se non la sai ancora, puoi lasciarla vuota.'}
      </p>
    </div>
  );
}
